/*
 * model.js — the Backbone-ish base Model that every entity descriptor and the
 * runtime record Model extend (recovered `Model(p)` from model.js:
 * get/set/changed/getSaveData/validation/mutations/defaults).
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
 * (defaults()/mutations()/validation()/getSaveData()/changed()/isNew() surface;
 * mutations run on construct AND on set; validation() maps attr -> Rule),
 * internal impl re-authored.
 */

/** Run a validation entry (Rule | Vuetify-fn | array-of-fns) against a value. */
function runRule(rule, value) {
  if (rule == null) return true;
  if (typeof rule.validate === 'function') return rule.validate(value); // Rule
  if (Array.isArray(rule)) {
    for (const r of rule) {
      const res = runRule(r, value);
      if (res !== true) return res;
    }
    return true;
  }
  if (typeof rule === 'function') {
    const res = rule(value);
    return res === undefined ? true : res;
  }
  return true;
}

export class Model {
  constructor(attributes = {}) {
    // non-enumerable bookkeeping so it never leaks into getSaveData()
    Object.defineProperty(this, '_changedKeys', { value: {}, writable: true, enumerable: false });
    Object.defineProperty(this, '_original', { value: {}, writable: true, enumerable: false });

    const defs = this._invoke('defaults');
    const merged = Object.assign({}, defs, attributes);
    const mut = this._invoke('mutations');

    for (const key of Object.keys(merged)) {
      let val = merged[key];
      if (typeof mut[key] === 'function') val = mut[key].call(this, val);
      this[key] = val;
    }
    this._original = this._snapshot();
  }

  /** call an override method if defined, else return {} */
  _invoke(name) {
    return typeof this[name] === 'function' ? (this[name]() || {}) : {};
  }

  /* ---- overridable hooks ---- */
  defaults() { return {}; }
  mutations() { return {}; }
  validation() { return {}; }

  /* ---- attribute plumbing ---- */
  _dataKeys() {
    return Object.keys(this).filter((k) => k.charAt(0) !== '_' && typeof this[k] !== 'function');
  }

  _snapshot() {
    const out = {};
    for (const k of this._dataKeys()) out[k] = this[k];
    return out;
  }

  get(key) {
    return this[key];
  }

  set(key, value) {
    if (key && typeof key === 'object') {
      Object.keys(key).forEach((k) => this.set(k, key[k]));
      return this;
    }
    const mut = this._invoke('mutations');
    let val = value;
    if (typeof mut[key] === 'function') val = mut[key].call(this, value);
    if (this[key] !== val) this._changedKeys[key] = true;
    this[key] = val;
    return this;
  }

  /** Plain-object view of the record's attributes (form binding source). */
  toObject() {
    return this._snapshot();
  }

  /** Payload for a save — shallow clone of attributes (kinds may override). */
  getSaveData() {
    return Object.assign({}, this._snapshot());
  }

  /** Has any attribute been set since construction / last reset? */
  changed() {
    return Object.keys(this._changedKeys).length > 0;
  }

  changedAttributes() {
    const out = {};
    Object.keys(this._changedKeys).forEach((k) => { out[k] = this[k]; });
    return out;
  }

  /** reset change tracking (after a successful save/fetch). */
  resetChanges() {
    this._changedKeys = {};
    this._original = this._snapshot();
    return this;
  }

  /** A record is "new" when it has no server id (or the placeholder "-"). */
  isNew() {
    const id = this.id;
    return id == null || id === '' || id === '-';
  }

  /**
   * validate([attrs]) -> { valid, errors:{field:message} }.
   * Runs each validation() entry; entries are Rule instances (from the
   * field-type registry) or Vuetify-style functions.
   */
  validate(attrs) {
    const source = attrs || this._snapshot();
    const rules = this._invoke('validation');
    const errors = {};
    for (const key of Object.keys(rules)) {
      const res = runRule(rules[key], source[key]);
      if (res !== true) errors[key] = res;
    }
    return { valid: Object.keys(errors).length === 0, errors };
  }
}

export default Model;
